import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Rnd } from 'react-rnd';
import { Minus, Square, X, RotateCcw, Maximize2 } from 'lucide-react';
import useResumePipStore, { type Rect } from '../store/useResumePipStore';

interface ResumeViewerProps {
    open: boolean;
    onClose: () => void;
    url: string | null;
    filename?: string;
}

type PaperRatio = 'letter' | 'a4';
type ZoomMode = 'fit' | number;

const US_LETTER_ASPECT_RATIO = 8.5 / 11;
const A4_ASPECT_RATIO = 210 / 297;
const VIEWPORT_HEIGHT_RATIO = 0.7;
const MIN_WIDTH = 320;
const BAR_HEIGHT = 36;

const FONT_SANS = 'var(--font-sans)';
const TEXT_PRIMARY = 'var(--text-primary)';
const TEXT_SECONDARY = 'var(--text-secondary)';
const TEXT_TERTIARY = 'var(--text-tertiary)';
const SURFACE_PRIMARY = 'var(--surface-primary)';
const SURFACE_SECONDARY = 'var(--surface-secondary)';
const SURFACE_TERTIARY = 'var(--surface-tertiary)';
const BORDER_DEFAULT = 'var(--border-default)';
const BORDER_MUTED = 'var(--border-muted)';
const FG_DANGER = 'var(--fg-danger)';
const BG_DANGER = 'var(--bg-danger)';

const getAspectRatio = (paper: PaperRatio) =>
    paper === 'letter' ? US_LETTER_ASPECT_RATIO : A4_ASPECT_RATIO;

const detectFileType = (url: string | null): 'pdf' | 'docx' | 'unknown' => {
    if (!url) return 'unknown';
    const lower = url.split('?')[0].toLowerCase();
    if (lower.endsWith('.pdf')) return 'pdf';
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'docx';
    if (lower.includes('.pdf')) return 'pdf';
    if (lower.includes('.docx') || lower.includes('.doc')) return 'docx';
    return 'unknown';
};

const buildPdfUrl = (
    baseUrl: string,
    opts: { page: number; zoomMode: ZoomMode; hideUi?: boolean },
) => {
    try {
        const u = new URL(baseUrl);
        const params = new URLSearchParams();
        params.set('page', String(opts.page));
        if (opts.zoomMode === 'fit') {
            params.set('zoom', 'page-fit');
            params.set('view', 'Fit');
        } else {
            params.set('zoom', String(opts.zoomMode));
        }
        if (opts.hideUi ?? true) {
            params.set('toolbar', '0');
            params.set('navpanes', '0');
            params.set('statusbar', '0');
            params.set('scrollbar', '0');
            params.set('pagemode', 'none');
        }
        u.hash = params.toString();
        return u.toString();
    } catch {
        return baseUrl;
    }
};

export default function ResumeViewer({ open, onClose, url, filename }: ResumeViewerProps) {
    const rndRef = useRef<Rnd>(null);
    const rect = useResumePipStore((s) => s.rect);
    const minimized = useResumePipStore((s) => s.minimized);
    const setRect = useResumePipStore((s) => s.setRect);
    const setMinimized = useResumePipStore((s) => s.setMinimized);

    const preMinimizeRectRef = useRef<Rect | null>(null);

    const [paper, setPaper] = useState<PaperRatio>('letter');
    const [zoomMode, setZoomMode] = useState<ZoomMode>('fit');

    const fileType = useMemo(() => detectFileType(url), [url]);

    const pdfUrl = useMemo(() => {
        if (!url || fileType !== 'pdf') return null;
        return buildPdfUrl(url, { page: 1, zoomMode, hideUi: true });
    }, [url, fileType, zoomMode]);

    const [iframeKey, setIframeKey] = useState(0);
    useEffect(() => {
        if (pdfUrl) setIframeKey((k) => k + 1);
    }, [pdfUrl]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey, { capture: true });
        return () => window.removeEventListener('keydown', onKey, { capture: true });
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;
        const onResize = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const effectiveHeight = minimized ? BAR_HEIGHT : rect.height;
            const newX = Math.min(rect.x, Math.max(20, vw - rect.width - 20));
            const newY = Math.min(rect.y, Math.max(20, vh - effectiveHeight - 20));
            if (newX !== rect.x || newY !== rect.y) {
                setRect({ ...rect, x: newX, y: newY });
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [open, minimized, rect, setRect]);

    const togglePaper = useCallback(() => {
        const next: PaperRatio = paper === 'letter' ? 'a4' : 'letter';
        setPaper(next);
        const newRatio = getAspectRatio(next);
        const newHeight = Math.round(rect.width / newRatio);
        setRect({ ...rect, height: newHeight });
    }, [paper, rect, setRect]);

    const resetPosition = useCallback(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const h = Math.round(vh * VIEWPORT_HEIGHT_RATIO);
        const w = Math.round(h * getAspectRatio(paper));
        setRect({
            x: Math.max(20, vw - w - 32),
            y: Math.max(20, Math.round((vh - h) / 2.5)),
            width: w,
            height: h,
        });
    }, [paper, setRect]);

    const toggleMinimize = useCallback(() => {
        if (!minimized) {
            preMinimizeRectRef.current = rect;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const clampedX = Math.min(rect.x, Math.max(20, vw - rect.width - 20));
            const clampedY = Math.min(rect.y, Math.max(20, vh - BAR_HEIGHT - 20));
            if (clampedX !== rect.x || clampedY !== rect.y) {
                setRect({ ...rect, x: clampedX, y: clampedY });
            }
        } else if (preMinimizeRectRef.current) {
            setRect(preMinimizeRectRef.current);
            preMinimizeRectRef.current = null;
        }
        setMinimized(!minimized);
    }, [minimized, rect, setRect, setMinimized]);

    const renderContent = () => {
        if (!url) {
            return (
                <div style={errorBoxStyle}>
                    <span style={{ fontSize: 12.5, color: TEXT_TERTIARY }}>No resume available.</span>
                </div>
            );
        }

        const iframeStyle: React.CSSProperties = {
            width: '100%',
            height: '100%',
            border: 'none',
            background: SURFACE_SECONDARY,
        };

        if (fileType === 'pdf') {
            return <iframe key={iframeKey} src={pdfUrl ?? url} style={iframeStyle} title="Resume" />;
        }

        if (fileType === 'docx') {
            const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
            return <iframe src={officeUrl} style={iframeStyle} title="Resume" />;
        }

        return <iframe src={url} style={iframeStyle} title="Resume" />;
    };

    const aspectRatio = getAspectRatio(paper);
    const minHeight = minimized ? BAR_HEIGHT : Math.round(MIN_WIDTH / aspectRatio);

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                pointerEvents: 'none',
                visibility: open ? 'visible' : 'hidden',
                fontFamily: FONT_SANS,
            }}
            aria-hidden={!open}
        >
            <Rnd
                ref={rndRef}
                style={{ pointerEvents: 'auto' }}
                size={{
                    width: rect.width,
                    height: minimized ? BAR_HEIGHT : rect.height,
                }}
                position={{ x: rect.x, y: rect.y }}
                minWidth={MIN_WIDTH}
                minHeight={minHeight}
                bounds="window"
                enableResizing={!minimized}
                lockAspectRatio={minimized ? false : aspectRatio}
                dragHandleClassName="lantern-resume-drag"
                cancel=".lantern-resume-no-drag"
                onDragStop={(_e, data) => {
                    setRect({ ...rect, x: data.x, y: data.y });
                }}
                onResizeStop={(_e, _direction, ref, _delta, position) => {
                    if (minimized) return;
                    setRect({
                        x: position.x,
                        y: position.y,
                        width: ref.offsetWidth,
                        height: ref.offsetHeight,
                    });
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        height: '100%',
                        width: '100%',
                        background: SURFACE_PRIMARY,
                        borderRadius: 12,
                        border: `1px solid ${BORDER_DEFAULT}`,
                        boxShadow: '0 20px 50px -10px rgba(40, 30, 20, 0.18), 0 6px 16px -4px rgba(40, 30, 20, 0.08)',
                        overflow: 'hidden',
                    }}
                >
                    {/* Top bar */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: BAR_HEIGHT,
                            background: SURFACE_PRIMARY,
                            borderBottom: minimized ? 'none' : `1px solid ${BORDER_MUTED}`,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 10px',
                            userSelect: 'none',
                            zIndex: 2,
                        }}
                    >
                        <div className="lantern-resume-no-drag" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <IconButton onClick={toggleMinimize} title={minimized ? 'Restore' : 'Minimize'}>
                                {minimized ? <Square size={11} strokeWidth={2} /> : <Minus size={12} strokeWidth={2} />}
                            </IconButton>
                            <IconButton onClick={onClose} title="Close" hoverDanger>
                                <X size={12} strokeWidth={2} />
                            </IconButton>
                        </div>

                        <div
                            className="lantern-resume-drag"
                            onDoubleClick={(e) => { e.stopPropagation(); toggleMinimize(); }}
                            style={{
                                flex: 1,
                                cursor: 'move',
                                fontSize: 11.5,
                                fontWeight: 500,
                                color: TEXT_SECONDARY,
                                textAlign: 'center',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                padding: '0 10px',
                            }}
                            title={filename || 'Resume'}
                        >
                            {filename || 'Resume'}
                        </div>

                        <div className="lantern-resume-no-drag" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {fileType === 'pdf' && !minimized && (
                                <>
                                    <IconButton onClick={togglePaper} title={`Switch to ${paper === 'letter' ? 'A4' : 'Letter'}`}>
                                        <span style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: 0.2 }}>
                                            {paper === 'letter' ? 'A4' : 'LT'}
                                        </span>
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setZoomMode('fit')}
                                        title="Fit to page"
                                        disabled={zoomMode === 'fit'}
                                    >
                                        <Maximize2 size={11} strokeWidth={2} />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setZoomMode(100)}
                                        title="100%"
                                        disabled={zoomMode === 100}
                                    >
                                        <span style={{ fontSize: 9.5, fontWeight: 600 }}>1:1</span>
                                    </IconButton>
                                </>
                            )}
                            <IconButton onClick={resetPosition} title="Reset position">
                                <RotateCcw size={11} strokeWidth={2} />
                            </IconButton>
                        </div>
                    </div>

                    {/* Content */}
                    <div
                        className="lantern-resume-no-drag"
                        style={{
                            position: 'absolute',
                            top: BAR_HEIGHT, left: 0, right: 0, bottom: 0,
                            background: SURFACE_SECONDARY,
                        }}
                    >
                        {!minimized && renderContent()}
                    </div>
                </div>
            </Rnd>
        </div>
    );
}

const errorBoxStyle: React.CSSProperties = {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: BG_DANGER,
    color: FG_DANGER,
    fontSize: 12.5,
    padding: 16,
    textAlign: 'center' as const,
};

interface IconButtonProps {
    onClick: (e: React.MouseEvent) => void;
    title: string;
    children: React.ReactNode;
    disabled?: boolean;
    hoverDanger?: boolean;
}

function IconButton({ onClick, title, children, disabled, hoverDanger }: IconButtonProps) {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) onClick(e);
            }}
            title={title}
            aria-label={title}
            disabled={disabled}
            style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                border: 'none',
                background: hover && !disabled
                    ? hoverDanger ? BG_DANGER : SURFACE_TERTIARY
                    : 'transparent',
                color: hover && hoverDanger ? FG_DANGER : disabled ? 'var(--text-quaternary)' : TEXT_SECONDARY,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'background 120ms, color 120ms',
                padding: 0,
                fontFamily: 'inherit',
            }}
        >
            {children}
        </button>
    );
}

